import { useContext, useId } from "react";
import { ChevronRight, Plus } from "lucide-react";
import type { CritereSaisi, QuestionSaisie } from "@repo/ent-struct";
import { Badge, Button, Label, Textarea } from "@repo/ui";
import { deplacer, remplacer, retirer } from "../edition";
import {
  ActionsElement,
  aDesProblemes,
  ChampNombre,
  ChampTexte,
  MessagesProbleme,
  ProblemesContext,
} from "./commun";

// `followup` (relance) n'est pas éditable : c'est l'IA qui relance. Le champ
// reste dans les données, le chatbot le reçoit tel quel.
const CRITERE_VIDE: CritereSaisi = { description: "", points: 1, followup: "" };

export const QUESTION_VIDE: QuestionSaisie = {
  type: "",
  texte: "",
  criteres: [CRITERE_VIDE],
};

function EditeurCritere({
  critere,
  chemin,
  numero,
  onChange,
  actions,
}: {
  critere: CritereSaisi;
  chemin: string;
  numero: number;
  onChange: (critere: CritereSaisi) => void;
  actions: React.ReactNode;
}) {
  const problemes = useContext(ProblemesContext);
  const invalide = aDesProblemes(problemes, chemin);
  return (
    <li className="rounded-surface border border-border p-3">
      <div className="flex items-start gap-3">
        <span className="mt-6 w-6 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
          {numero}.
        </span>
        <div className="grid min-w-0 flex-1 gap-2 md:grid-cols-[minmax(0,1fr)_6rem]">
          <ChampTexte
            label="Critère attendu"
            valeur={critere.description}
            invalide={invalide}
            onChange={(description) => onChange({ ...critere, description })}
          />
          <ChampNombre
            label="Points"
            pas={1}
            valeur={critere.points}
            invalide={invalide}
            onChange={(points) => onChange({ ...critere, points })}
          />
        </div>
        <div className="mt-5 shrink-0">{actions}</div>
      </div>
      <MessagesProbleme chemin={chemin} />
    </li>
  );
}

/**
 * Une question, repliable : une scorecard en compte une quinzaine, avec leurs
 * critères. Le résumé (ID + texte) suffit pour s'y retrouver.
 */
export function EditeurQuestion({
  question,
  chemin,
  numero,
  onChange,
  actions,
}: {
  question: QuestionSaisie;
  chemin: string;
  numero: number;
  onChange: (question: QuestionSaisie) => void;
  actions: React.ReactNode;
}) {
  const idTexte = useId();
  const problemes = useContext(ProblemesContext);
  // Une erreur plus bas dans l'arbre s'annonce sur le résumé replié.
  const enErreur = [...problemes.keys()].some(
    (c) => c === chemin || c.startsWith(`${chemin}.`),
  );
  const setCriteres = (criteres: CritereSaisi[]) =>
    onChange({ ...question, criteres });

  return (
    <li>
      <details
        className={`group rounded-surface border bg-card ${enErreur ? "border-destructive" : "border-charte-line-strong"}`}
      >
        <summary className="flex cursor-pointer list-none items-center gap-2 p-3 hover:bg-charte-hover">
          <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
          <Badge variant="outline" className="shrink-0 font-mono">
            {question.questionId ?? "nouvelle"}
          </Badge>
          <span className="min-w-0 flex-1 truncate text-sm">
            {question.texte || (
              <span className="italic text-muted-foreground">Question {numero} sans texte</span>
            )}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {question.criteres.length} critère{question.criteres.length > 1 ? "s" : ""}
          </span>
          {/* Les actions ne doivent pas replier la carte. */}
          <div onClick={(e) => e.preventDefault()}>{actions}</div>
        </summary>

        <div className="space-y-3 border-t border-border p-3">
          {/* `min-w-0` : sans lui, un texte long élargit la colonne au-delà
              de la page (le textarea se dimensionne sur son contenu). */}
          <div className="grid gap-2 md:grid-cols-[8rem_minmax(0,1fr)]">
            <ChampTexte
              label="Type"
              valeur={question.type}
              placeholder="Comp., Situa."
              onChange={(type) => onChange({ ...question, type })}
            />
            <div className="min-w-0">
              <Label htmlFor={idTexte} className="mb-1 text-xs text-muted-foreground">
                Question posée au candidat
              </Label>
              <Textarea
                id={idTexte}
                fieldSizing="content"
                value={question.texte}
                aria-invalid={aDesProblemes(problemes, chemin) || undefined}
                onChange={(e) => onChange({ ...question, texte: e.target.value })}
              />
            </div>
          </div>
          <MessagesProbleme chemin={chemin} />

          <ol className="space-y-2">
            {question.criteres.map((critere, i) => (
              <EditeurCritere
                key={i}
                critere={critere}
                chemin={`${chemin}.criteres[${i}]`}
                numero={i + 1}
                onChange={(c) => setCriteres(remplacer(question.criteres, i, c))}
                actions={
                  <ActionsElement
                    libelle={`le critère ${i + 1}`}
                    index={i}
                    total={question.criteres.length}
                    onDeplacer={(sens) => setCriteres(deplacer(question.criteres, i, sens))}
                    onSupprimer={() => setCriteres(retirer(question.criteres, i))}
                  />
                }
              />
            ))}
          </ol>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCriteres([...question.criteres, CRITERE_VIDE])}
          >
            <Plus /> Ajouter un critère
          </Button>
        </div>
      </details>
    </li>
  );
}

/** Liste de questions d'une compétence plate ou d'une sous-compétence. */
export function ListeQuestions({
  questions,
  chemin,
  onChange,
}: {
  questions: QuestionSaisie[];
  /** Chemin du parent : les questions sont à `${chemin}.questions[i]`. */
  chemin: string;
  onChange: (questions: QuestionSaisie[]) => void;
}) {
  return (
    <div className="space-y-2">
      <ol className="space-y-2">
        {questions.map((question, i) => (
          <EditeurQuestion
            key={i}
            question={question}
            chemin={`${chemin}.questions[${i}]`}
            numero={i + 1}
            onChange={(q) => onChange(remplacer(questions, i, q))}
            actions={
              <ActionsElement
                libelle={`la question ${i + 1}`}
                index={i}
                total={questions.length}
                onDeplacer={(sens) => onChange(deplacer(questions, i, sens))}
                onSupprimer={() => onChange(retirer(questions, i))}
              />
            }
          />
        ))}
      </ol>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...questions, QUESTION_VIDE])}
      >
        <Plus /> Ajouter une question
      </Button>
    </div>
  );
}
